import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Icon,
  InputField,
  Loader,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import { SliderComponent } from '../../../../../../shared/components';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import {
  type AccountRole,
  type RoleAuthObject,
  useLazySearchAccountRolesQuery,
  useLazySearchRoleAuthObjectsQuery,
} from '../../../../data-layer/licenseManagementApi';
import type {
  RoleIntelligenceChildRole,
  RoleIntelligenceRow,
} from '../../../../mock-data/roleIntelligenceRolesTableMockData';
import { useInfiniteScroll } from '../../FUEInventory/FUEInventoryTable/hooks/useInfiniteScroll';

import './RoleIntelligencePanel.css';

interface RoleIntelligencePanelProps {
  isOpen: boolean;
  onClose: () => void;
  role: RoleIntelligenceRow | null;
}

interface RoleDrilldownPath {
  roleName: string;
  roleKey: number;
  parentRoleName?: string;
}

/** Extended auth object type with optional fueScore for single role view */
interface RoleAuthObjectWithScore extends RoleAuthObject {
  fueScore?: number;
}

/** Auth objects drill-down view (reused pattern from FUEInventoryUserPanel) */
function RoleAuthObjectsDrilldown({
  drilldownPath,
  onBack,
}: {
  drilldownPath: RoleDrilldownPath;
  onBack: () => void;
}) {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  const [allAuthObjects, setAllAuthObjects] = useState<RoleAuthObject[]>([]);
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchRoleAuthObjectsQuery();
  const firstPageLoaded = useRef(false);

  useEffect(() => {
    if (firstPageLoaded.current) {
      return;
    }

    firstPageLoaded.current = true;
    setIsFirstPageLoading(true);

    fetchMore({
      vendor: 'sap',
      roleKey: drilldownPath.roleKey,
      cloudLicenseKey,
      searchAfter: null,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          setAllAuthObjects(result.data.authObjects ?? []);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFirstPageLoading(false);
      });
  }, [fetchMore, drilldownPath.roleKey, cloudLicenseKey]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor: 'sap',
      roleKey: drilldownPath.roleKey,
      cloudLicenseKey,
      searchAfter: nextSearchAfter,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.authObjects ?? [];

          setAllAuthObjects((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [
    nextSearchAfter,
    isFetchingMore,
    fetchMore,
    drilldownPath.roleKey,
    cloudLicenseKey,
  ]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  return (
    <div className='LicenseManagement-RoleIntelligencePanel-drilldown'>
      <button
        type='button'
        className='LicenseManagement-RoleIntelligencePanel-drilldown-back'
        onClick={onBack}>
        <Icon kind='ChevronLeft' size='xSmall' />
        <Typography
          kind='body3'
          className='LicenseManagement-RoleIntelligencePanel-drilldown-backText'>
          {localize('Back to roles')}
        </Typography>
      </button>

      <div className='LicenseManagement-RoleIntelligencePanel-drilldown-path'>
        <Typography
          kind='body3'
          className='LicenseManagement-RoleIntelligencePanel-drilldown-pathText'>
          {[drilldownPath.parentRoleName, drilldownPath.roleName]
            .filter(Boolean)
            .join('  >  ')}
        </Typography>
      </div>

      <div className='LicenseManagement-RoleIntelligencePanel-drilldown-tableHeader'>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colAuthObj'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('AUTH OBJECT')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colField'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('FIELD')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colValue'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('VALUE')}
          </Typography>
        </div>
      </div>

      {isFirstPageLoading && (
        <div className='LicenseManagement-RoleIntelligencePanel-loader'>
          <Loader kind='loop' format='inline' color='brand' />
        </div>
      )}

      {!isFirstPageLoading && allAuthObjects.length === 0 && (
        <Typography
          kind='body2'
          className='LicenseManagement-RoleIntelligencePanel-empty'>
          {localize('No auth objects found.')}
        </Typography>
      )}

      {allAuthObjects.map((authObj) => (
        <div
          key={authObj.authObjectKey}
          className='LicenseManagement-RoleIntelligencePanel-drilldown-row'>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colAuthObj'>
            <Typography kind='body2'>
              <span title={authObj.authObjectName}>
                {authObj.authObjectName}
              </span>
            </Typography>
            {authObj.fields.length > 1 && (
              <Typography
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-fieldCount'>
                {authObj.fields.length} {localize('fields')}
              </Typography>
            )}
          </div>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colField'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-f-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-fieldText'>
                <span title={f.field}>{f.field}</span>
              </Typography>
            ))}
          </div>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colValue'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-v-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-valueText'>
                <span title={f.value}>{f.value}</span>
              </Typography>
            ))}
          </div>
        </div>
      ))}

      <div
        ref={sentinelRef}
        className='LicenseManagement-RoleIntelligencePanel-sentinel'>
        {isFetchingMore && <Loader kind='loop' format='inline' color='brand' />}
      </div>
    </div>
  );
}

interface RoleIntelligencePanelProps {
  isOpen: boolean;
  onClose: () => void;
  role: RoleIntelligenceRow | null;
}

/** Composite roles table with infinite scroll (same pattern as FUE Inventory) */
function CompositeRolesTable({
  roleKey,
  parentRoleName,
  onRoleDetailClick,
}: {
  roleKey: number;
  parentRoleName: string;
  onRoleDetailClick: (
    child: RoleIntelligenceChildRole,
    parentName?: string
  ) => void;
}) {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  const [allRoles, setAllRoles] = useState<AccountRole[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchAccountRolesQuery();
  const firstPageLoaded = useRef(false);

  useEffect(() => {
    if (firstPageLoaded.current) {
      return;
    }

    firstPageLoaded.current = true;
    setIsFirstPageLoading(true);

    fetchMore({
      vendor: 'sap',
      accountKey: roleKey,
      cloudLicenseKey,
      searchAfter: null,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          setAllRoles(result.data.roles ?? []);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFirstPageLoading(false);
      });
  }, [fetchMore, roleKey, cloudLicenseKey]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor: 'sap',
      accountKey: roleKey,
      cloudLicenseKey,
      searchAfter: nextSearchAfter,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.roles ?? [];

          setAllRoles((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [nextSearchAfter, isFetchingMore, fetchMore, roleKey, cloudLicenseKey]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  // Client-side filter of the loaded roles by name (case-insensitive)
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleRoles = normalizedSearch
    ? allRoles.filter((child) =>
        child.roleName.toLowerCase().includes(normalizedSearch)
      )
    : allRoles;

  if (isFirstPageLoading) {
    return (
      <div className='LicenseManagement-RoleIntelligencePanel-loader'>
        <Loader kind='loop' format='inline' color='brand' />
      </div>
    );
  }

  return (
    <div className='LicenseManagement-RoleIntelligencePanel-rolesTable'>
      <div className='LicenseManagement-RoleIntelligencePanel-rolesSearch'>
        <InputField
          name='roleIntelligenceRoleSearch'
          value={searchTerm}
          setValue={setSearchTerm}
          placeholder={localize('Search roles...')}
          size='large'
          prefixIcon={<Icon kind='Search' size='medium' />}
        />
      </div>
      <div className='LicenseManagement-RoleIntelligencePanel-rolesHeader'>
        <div className='LicenseManagement-RoleIntelligencePanel-rolesHeaderCell LicenseManagement-RoleIntelligencePanel-rolesHeaderCell--name'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('ASSIGNED ROLE')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-rolesHeaderCell LicenseManagement-RoleIntelligencePanel-rolesHeaderCell--score'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('FUE SCORE')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-rolesHeaderCell LicenseManagement-RoleIntelligencePanel-rolesHeaderCell--detail' />
      </div>
      {visibleRoles.length === 0 && (
        <Typography
          kind='body2'
          className='LicenseManagement-RoleIntelligencePanel-empty'>
          {localize('No roles found.')}
        </Typography>
      )}
      {visibleRoles.map((child) => (
        <div
          key={child.roleKey}
          className='LicenseManagement-RoleIntelligencePanel-roleRow'>
          <div className='LicenseManagement-RoleIntelligencePanel-roleNameGroup'>
            <Typography kind='body2'>
              <span title={child.roleName}>{child.roleName}</span>
            </Typography>
          </div>
          <Typography
            kind='body2'
            className='LicenseManagement-RoleIntelligencePanel-fueScore'>
            {child.tierWeight}
          </Typography>
          <button
            type='button'
            className='LicenseManagement-RoleIntelligencePanel-roleDetailBtn'
            onClick={() =>
              onRoleDetailClick(
                {
                  roleKey: child.roleKey,
                  roleName: child.roleName,
                  tierWeight: child.tierWeight,
                },
                parentRoleName
              )
            }>
            <Icon
              kind='ChevronRight'
              size='small'
              className='LicenseManagement-RoleIntelligencePanel-roleArrowIcon'
            />
          </button>
        </div>
      ))}
      <div
        ref={sentinelRef}
        className='LicenseManagement-RoleIntelligencePanel-sentinel'>
        {isFetchingMore && <Loader kind='loop' format='inline' color='brand' />}
      </div>
    </div>
  );
}

/** Auth objects table for single roles (inline, no back button) */
function SingleRoleAuthObjects({ roleKey }: { roleKey: number }) {
  const localize = useInvertedLocalize();
  const cloudLicenseKey = useTypedSelector(
    (state) => state.aag.licenseManagement.cloudLicenseKey
  );

  const [allAuthObjects, setAllAuthObjects] = useState<RoleAuthObject[]>([]);
  const [nextSearchAfter, setNextSearchAfter] = useState<string[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFirstPageLoading, setIsFirstPageLoading] = useState(false);
  const [fetchMore, { isFetching: isFetchingMore }] =
    useLazySearchRoleAuthObjectsQuery();
  const firstPageLoaded = useRef(false);

  useEffect(() => {
    if (firstPageLoaded.current) {
      return;
    }

    firstPageLoaded.current = true;
    setIsFirstPageLoading(true);

    fetchMore({
      vendor: 'sap',
      roleKey,
      cloudLicenseKey,
      searchAfter: null,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          setAllAuthObjects(result.data.authObjects ?? []);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFirstPageLoading(false);
      });
  }, [fetchMore, roleKey, cloudLicenseKey]);

  const loadNextPage = useCallback(() => {
    if (!nextSearchAfter || isFetchingMore) {
      return;
    }

    fetchMore({
      vendor: 'sap',
      roleKey,
      cloudLicenseKey,
      searchAfter: nextSearchAfter,
      pageSize: 20,
    })
      .then((result) => {
        if (result.data) {
          const newRows = result.data.authObjects ?? [];

          setAllAuthObjects((prev) => [...prev, ...newRows]);
          const cursor = result.data.searchAfter ?? null;

          setNextSearchAfter(cursor);
          setHasMore(cursor !== null);
        }
      })
      .catch(() => {});
  }, [nextSearchAfter, isFetchingMore, fetchMore, roleKey, cloudLicenseKey]);

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: loadNextPage,
  });

  if (isFirstPageLoading) {
    return (
      <div className='LicenseManagement-RoleIntelligencePanel-loader'>
        <Loader kind='loop' format='inline' color='brand' />
      </div>
    );
  }

  return (
    <div className='LicenseManagement-RoleIntelligencePanel-rolesTable'>
      <div className='LicenseManagement-RoleIntelligencePanel-drilldown-tableHeader'>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colAuthObj'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('AUTH OBJECT')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colFueScore'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('FUE SCORE')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colField'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('FIELD')}
          </Typography>
        </div>
        <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colValue'>
          <Typography
            kind='body3'
            className='LicenseManagement-RoleIntelligencePanel-label'>
            {localize('VALUE')}
          </Typography>
        </div>
      </div>

      {!isFirstPageLoading && allAuthObjects.length === 0 && (
        <Typography
          kind='body2'
          className='LicenseManagement-RoleIntelligencePanel-empty'>
          {localize('No auth objects found.')}
        </Typography>
      )}

      {allAuthObjects.map((authObj) => (
        <div
          key={authObj.authObjectKey}
          className='LicenseManagement-RoleIntelligencePanel-drilldown-row'>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colAuthObj'>
            <Typography kind='body2'>
              <span title={authObj.authObjectName}>
                {authObj.authObjectName}
              </span>
            </Typography>
            {authObj.fields.length > 1 && (
              <Typography
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-fieldCount'>
                {authObj.fields.length} {localize('fields')}
              </Typography>
            )}
          </div>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colFueScore'>
            <Typography
              kind='body3'
              className='LicenseManagement-RoleIntelligencePanel-fueScore'>
              {(authObj as RoleAuthObjectWithScore).fueScore ?? '—'}
            </Typography>
          </div>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colField'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-f-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-fieldText'>
                <span title={f.field}>{f.field}</span>
              </Typography>
            ))}
          </div>
          <div className='LicenseManagement-RoleIntelligencePanel-drilldown-colValue'>
            {authObj.fields.map((f) => (
              <Typography
                key={`${authObj.authObjectKey}-v-${f.field}-${f.value}`}
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-drilldown-valueText'>
                <span title={f.value}>{f.value}</span>
              </Typography>
            ))}
          </div>
        </div>
      ))}

      <div
        ref={sentinelRef}
        className='LicenseManagement-RoleIntelligencePanel-sentinel'>
        {isFetchingMore && <Loader kind='loop' format='inline' color='brand' />}
      </div>
    </div>
  );
}

/**
 * RoleIntelligencePanel component
 * @description Displays detailed information about a role in a side panel, including FUE tier,
 * assigned users, annual cost, authorization objects, and optimization opportunities.
 * Shows cost savings calculations based on FUE weight optimization.
 * @param {RoleIntelligencePanelProps} props - Component props
 * @param {boolean} props.isOpen - Controls the visibility of the side panel
 * @param {() => void} props.onClose - Callback triggered when the panel is closed
 * @param {RoleIntelligenceRow | null} props.role - The role data to display, or null if no role is selected
 * @returns {React.ReactElement | null} The rendered side panel, or null if no role is provided
 */
function RoleIntelligencePanel({
  isOpen,
  onClose,
  role,
}: RoleIntelligencePanelProps): React.ReactElement | null {
  const localize = useInvertedLocalize();
  const [drilldownPath, setDrilldownPath] = useState<RoleDrilldownPath | null>(
    null
  );

  // Reset drilldown when panel closes or role changes
  useEffect(() => {
    if (!isOpen) {
      setDrilldownPath(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setDrilldownPath(null);
  }, [role]);

  if (!role) {
    return null;
  }

  const handleRoleDetailClick = (
    childRole: RoleIntelligenceChildRole,
    parentName?: string
  ) => {
    setDrilldownPath({
      roleName: childRole.roleName,
      roleKey: childRole.roleKey,
      parentRoleName: parentName,
    });
  };

  const annualCost = role.costPerUser * role.users;
  const fueTierDisplay = role.currentTier.split(' ')[0];

  const optimizationFueSavings = role.optimization
    ? (role.optimization.currentFueWeight -
        role.optimization.optimizedFueWeight) *
      role.users
    : 0;

  const annualCostDisplay = `$${annualCost.toLocaleString()}`;
  const currentFueWeightDisplay = role.optimization
    ? `${role.optimization.currentFueWeight} (${role.optimization.currentTierLabel})`
    : '';
  const optimizedFueWeightDisplay = role.optimization
    ? `${role.optimization.optimizedFueWeight} (${role.optimization.optimizedTierLabel})`
    : '';
  const estimatedSavingsLabel = `${localize('Estimated savings')} (${
    role.users
  } ${localize('users')})`;
  const savingsValueDisplay = `${optimizationFueSavings.toFixed(
    1
  )} FUE (~$${role.totalSavings.toLocaleString()}/yr)`;

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      header={{
        title: role.roleName,
        description: `${role.type} Role`,
      }}
      layout={
        <div className='LicenseManagement-RoleIntelligencePanel'>
          {drilldownPath && (
            <RoleAuthObjectsDrilldown
              drilldownPath={drilldownPath}
              onBack={() => setDrilldownPath(null)}
            />
          )}
          <div
            className='LicenseManagement-RoleIntelligencePanel-body'
            style={{ display: drilldownPath ? 'none' : undefined }}>
            {/* Section 1: FUE Tier + Assigned Users */}
            <div className='LicenseManagement-RoleIntelligencePanel-section'>
              <div className='LicenseManagement-RoleIntelligencePanel-grid'>
                <div className='LicenseManagement-RoleIntelligencePanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-RoleIntelligencePanel-label'>
                    {localize('FUE Tier')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-RoleIntelligencePanel-value'>
                    {fueTierDisplay}
                  </Typography>
                </div>
                <div className='LicenseManagement-RoleIntelligencePanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-RoleIntelligencePanel-label'>
                    {localize('Assigned Users')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-RoleIntelligencePanel-value'>
                    {role.users}
                  </Typography>
                </div>
              </div>
            </div>

            <div className='LicenseManagement-RoleIntelligencePanel-divider' />

            {/* Section 2: Annual Cost + Description */}
            <div className='LicenseManagement-RoleIntelligencePanel-section'>
              <div className='LicenseManagement-RoleIntelligencePanel-grid LicenseManagement-RoleIntelligencePanel-grid--autoHeight'>
                <div className='LicenseManagement-RoleIntelligencePanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-RoleIntelligencePanel-label'>
                    {localize('Annual Cost')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-RoleIntelligencePanel-value'>
                    {annualCostDisplay}
                  </Typography>
                </div>
                <div className='LicenseManagement-RoleIntelligencePanel-field'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-RoleIntelligencePanel-label'>
                    {localize('Description')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-RoleIntelligencePanel-value'>
                    {role.roleDescription}
                  </Typography>
                </div>
              </div>
            </div>

            {/* Optimization Card */}
            {role.optimization && (
              <div className='LicenseManagement-RoleIntelligencePanel-section'>
                <div className='LicenseManagement-RoleIntelligencePanel-optimizationCard'>
                  <Typography
                    kind='body3'
                    className='LicenseManagement-RoleIntelligencePanel-optimizationTitle'>
                    {localize('Optimization Available')}
                  </Typography>
                  <Typography
                    kind='body2'
                    className='LicenseManagement-RoleIntelligencePanel-optimizationDesc'>
                    {role.optimization.description}
                  </Typography>

                  <div className='LicenseManagement-RoleIntelligencePanel-grid LicenseManagement-RoleIntelligencePanel-grid--autoHeight LicenseManagement-RoleIntelligencePanel-fueWeightGrid'>
                    <div className='LicenseManagement-RoleIntelligencePanel-field'>
                      <Typography
                        kind='body3'
                        className='LicenseManagement-RoleIntelligencePanel-label'>
                        {localize('Current FUE Weight')}
                      </Typography>
                      <SliderComponent
                        value={role.optimization.currentFueWeight * 100}
                        readOnly
                        darkMode
                        progressColor='#EF4444'
                        trackHeight='0.25rem'
                      />
                      <Typography
                        kind='body4'
                        className='LicenseManagement-RoleIntelligencePanel-value'>
                        {currentFueWeightDisplay}
                      </Typography>
                    </div>
                    <div className='LicenseManagement-RoleIntelligencePanel-field'>
                      <Typography
                        kind='body3'
                        className='LicenseManagement-RoleIntelligencePanel-label'>
                        {localize('Optimized FUE Weight')}
                      </Typography>
                      <SliderComponent
                        value={role.optimization.optimizedFueWeight * 100}
                        readOnly
                        darkMode
                        progressColor='#22C55E'
                        trackHeight='0.25rem'
                      />
                      <Typography
                        kind='body4'
                        className='LicenseManagement-RoleIntelligencePanel-value'>
                        {optimizedFueWeightDisplay}
                      </Typography>
                    </div>
                  </div>

                  <div className='LicenseManagement-RoleIntelligencePanel-optimizationDivider' />

                  <div className='LicenseManagement-RoleIntelligencePanel-savingsRow'>
                    <Typography
                      kind='body4'
                      className='LicenseManagement-RoleIntelligencePanel-label'>
                      {estimatedSavingsLabel}
                    </Typography>
                    <Typography
                      kind='body4'
                      className='LicenseManagement-RoleIntelligencePanel-savingsValue'>
                      {savingsValueDisplay}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
            {/* <div className='LicenseManagement-RoleIntelligencePanel-divider' /> */}
            {/* Roles / Auth Objects Section */}
            <div className='LicenseManagement-RoleIntelligencePanel-section'>
              {/* <Typography
                kind='body3'
                className='LicenseManagement-RoleIntelligencePanel-sectionTitle'>
                {role.type === 'Composite'
                  ? localize('Child Roles')
                  : localize('Authorization Objects')}
              </Typography> */}

              {role.type === 'Composite' ? (
                <CompositeRolesTable
                  roleKey={Number(role.id)}
                  parentRoleName={role.roleName}
                  onRoleDetailClick={handleRoleDetailClick}
                />
              ) : (
                <SingleRoleAuthObjects roleKey={Number(role.id)} />
              )}
            </div>
          </div>

          {/* Footer - hidden during drilldown */}
          {!drilldownPath && (
            <div className='LicenseManagement-RoleIntelligencePanel-footer'>
              <Button kind='ghost' onClick={onClose}>
                {localize('Close')}
              </Button>
            </div>
          )}
        </div>
      }
      className='LicenseManagement-RoleIntelligencePanel-sidePanel'
    />
  );
}

export default RoleIntelligencePanel;
